import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AccessPermission,
  SystemModule,
  listModules,
  listPermissionsCatalog,
} from '../../services/accessControlApi';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';

const permissionTypeLabels: Record<string, string> = {
  access: 'Acesso ao módulo',
  read: 'Leitura',
  write: 'Escrita',
};

export function ModulesAdminPage() {
  const [modules, setModules] = useState<SystemModule[]>([]);
  const [permissions, setPermissions] = useState<AccessPermission[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [moduleResult, permissionItems] = await Promise.all([
          listModules({ page, pageSize }),
          listPermissionsCatalog(),
        ]);
        setModules(moduleResult.items);
        setTotalCount(moduleResult.totalCount);
        setPage(moduleResult.page);
        setPermissions(permissionItems);
      } catch {
        toast.error('Falha ao carregar módulos.');
      } finally {
        setLoading(false);
      }
    })();
  }, [page, pageSize]);

  const permissionsByModule = useMemo(() => {
    const map = new Map<string, AccessPermission[]>();
    for (const permission of permissions) {
      const list = map.get(permission.moduleCode) ?? [];
      list.push(permission);
      map.set(permission.moduleCode, list);
    }

    for (const list of Array.from(map.values())) {
      list.sort((left, right) => left.type.localeCompare(right.type));
    }

    return map;
  }, [permissions]);

  const sortedModules = useMemo(
    () => [...modules].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name)),
    [modules],
  );
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (loading) {
    return <p className="erp-loading">Carregando...</p>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Módulos do sistema"
        description="Catálogo de módulos registrados e permissões disponíveis para atribuição aos perfis de acesso. Novos módulos são registrados pelo seed/deploy da aplicação."
      />

      <div className="erp-table-wrap">
        <div className="erp-table-scroll">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Código</th>
                <th>Pilar</th>
                <th>Rota</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedModules.map((module) => (
                <tr key={module.id}>
                  <td className="font-medium text-secondary-900">{module.name}</td>
                  <td className="font-mono text-xs text-secondary-600">{module.code}</td>
                  <td>{module.pillar}</td>
                  <td className="font-mono text-xs text-secondary-600">{module.routePath}</td>
                  <td>
                    <ActiveStatusBadge active={module.isActive} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="erp-card-title">Permissões por módulo</h2>
        {sortedModules.map((module) => {
          const modulePermissions = permissionsByModule.get(module.code) ?? [];
          return (
            <section key={module.id} className="erp-card">
              <div className="erp-card-body">
              <div className="mb-3 flex flex-wrap items-baseline gap-2">
                <h3 className="font-semibold text-secondary-900">{module.name}</h3>
                <span className="font-mono text-xs text-secondary-500">{module.code}</span>
              </div>
              {modulePermissions.length === 0 ? (
                <p className="text-sm text-secondary-500">Nenhuma permissão registrada.</p>
              ) : (
                <div className="erp-table-wrap border-secondary-100">
                  <div className="erp-table-scroll">
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th>Permissão</th>
                          <th>Código</th>
                          <th>Tipo</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modulePermissions.map((permission) => (
                          <tr key={permission.id}>
                            <td>{permission.name}</td>
                            <td className="font-mono text-xs text-secondary-600">{permission.code}</td>
                            <td>{permissionTypeLabels[permission.type] ?? permission.type}</td>
                            <td>
                              <ActiveStatusBadge active={permission.isActive} feminine />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              </div>
            </section>
          );
        })}
      </div>
      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
